import React, { type ReactNode } from 'react';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { RedirectToArchivedPage } from '@atlassian/jira-redirect-archived-project/src/ui/index.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { AuthenticationCheck } from '@atlassian/jira-spa-apps-common/src/interceptors/authentication';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { Redirect } from '@atlassian/react-resource-router';
import { ProjectContextSubscriber } from '../project-context';

export type Props = {
	projectKey: string | null | undefined;
	children: ReactNode;
	isRouteAllowedForNonAdmins?: () => boolean;
	teamName?: string;
	packageName?: string;
	packageId?: string;
};

const ERROR_REPORTING_PACKAGE_ID = 'projectSettingsController';
const ERROR_REPORTING_PACKAGE_NAME = 'jiraSpaAppsSoftwareProjectSettingsController';

export const ProjectSettingsControllerOld = ({
	projectKey,
	isRouteAllowedForNonAdmins,
	children,
}: Props) => (
	<JSErrorBoundary
		id={ERROR_REPORTING_PACKAGE_ID}
		teamName="morphinecake"
		packageName={ERROR_REPORTING_PACKAGE_NAME}
		sendToPrivacyUnsafeSplunk
	>
		<AuthenticationCheck>
			<RedirectNonAdminsAndArchivedProjects
				projectKey={projectKey}
				isRouteAllowedForNonAdmins={isRouteAllowedForNonAdmins}
			>
				{children}
			</RedirectNonAdminsAndArchivedProjects>
		</AuthenticationCheck>
	</JSErrorBoundary>
);

export const ProjectSettingsControllerNew = ({
	projectKey,
	isRouteAllowedForNonAdmins,
	children,
	packageId = ERROR_REPORTING_PACKAGE_ID,
	packageName = ERROR_REPORTING_PACKAGE_NAME,
	teamName = 'morphinecake',
}: Props) => (
	<JSErrorBoundary
		id={ERROR_REPORTING_PACKAGE_ID}
		teamName="morphinecake"
		packageName={ERROR_REPORTING_PACKAGE_NAME}
		sendToPrivacyUnsafeSplunk
	>
		<AuthenticationCheck>
			<RedirectNonAdminsAndArchivedProjects
				projectKey={projectKey}
				isRouteAllowedForNonAdmins={isRouteAllowedForNonAdmins}
			>
				<JSErrorBoundary
					id={packageId}
					teamName={teamName}
					packageName={packageName}
					sendToPrivacyUnsafeSplunk
				>
					{children}
				</JSErrorBoundary>
			</RedirectNonAdminsAndArchivedProjects>
		</AuthenticationCheck>
	</JSErrorBoundary>
);

/**
 * Filter for all project settings routes.
 * This function will redirect depending on the state of a project and the permissions of the tenant.
 * Project settings are accessible once these conditions are met:
 * 	- projectKey is defined
 * 	- The user is authenticated (via AuthenticationCheck)
 * 	- The user is an admin and the project is not archived (via RedirectNonAdminsAndArchivedProjects)
 * If not met, a redirect is issued.
 */
export const ProjectSettingsController = componentWithFG(
	'projectsettingscontroller_error_boundary',
	ProjectSettingsControllerNew,
	ProjectSettingsControllerOld,
);

const RedirectNonAdminsAndArchivedProjectsNew = ({
	projectKey,
	isRouteAllowedForNonAdmins,
	children,
}: Props) => (
	<TenantContextSubscriber>
		{({ tenantContext }) => {
			if (!projectKey) {
				return <Redirect to="/jira/projects" />;
			}

			return (
				<ProjectContextSubscriber>
					{/* @ts-expect-error - TS2322 - Type '(projectContext: ProjectContext) => React.ReactNode' is not assignable to type '(arg1: ProjectContext) => ReactElement<any, string | JSXElementConstructor<any>>'. */}
					{(projectContext: ProjectContext) => {
						if (projectContext.isProjectArchived) {
							return <RedirectToArchivedPage />;
						}
						if (
							tenantContext.isAdmin ||
							projectContext.isProjectAdmin ||
							(isRouteAllowedForNonAdmins && isRouteAllowedForNonAdmins())
						) {
							return children;
						}

						return <Redirect to={`/projects/${projectContext.projectKey}`} />;
					}}
				</ProjectContextSubscriber>
			);
		}}
	</TenantContextSubscriber>
);

const RedirectNonAdminsAndArchivedProjectsOld = ({ projectKey, children }: Props) => (
	<TenantContextSubscriber>
		{({ tenantContext }) => {
			if (!projectKey) {
				return <Redirect to="/jira/projects" />;
			}

			return (
				<ProjectContextSubscriber>
					{/* @ts-expect-error - TS2322 - Type '(projectContext: ProjectContext) => React.ReactNode' is not assignable to type '(arg1: ProjectContext) => ReactElement<any, string | JSXElementConstructor<any>>'. */}
					{(projectContext: ProjectContext) => {
						if (projectContext.isProjectArchived) {
							return <RedirectToArchivedPage />;
						}
						if (tenantContext.isAdmin || projectContext.isProjectAdmin) {
							return children;
						}

						return <Redirect to={`/projects/${projectContext.projectKey}`} />;
					}}
				</ProjectContextSubscriber>
			);
		}}
	</TenantContextSubscriber>
);

const RedirectNonAdminsAndArchivedProjects = componentWithFF(
	'olympus-da-157-enable-project-settings-navigation_zxaq5',
	RedirectNonAdminsAndArchivedProjectsNew,
	RedirectNonAdminsAndArchivedProjectsOld,
);
